import {
  CampaignBody,
  CampaignPreferredImageEnum,
  CampaignType,
  CategoryFilterType,
  CategorySensitivityColumns,
} from "@vviinn/user-managment-client-ts";

import {
  SwitchableSettingsTitles,
  NumericSettingsTitles,
  SelectSettingsTitles,
  Selector,
  SimilarityAITitles,
  ColorAiTitles,
  TextCampaignInfluencersTitles,
  TextCampaignInfluencersKeys,
  CampaignNetKey,
} from "./types";

import {
  minimalTextTitleColor,
  minimalTextTitleSimilarity,
  netTextSurrounding,
  keysWithTitles,
} from "../helpers";
import { valuesToSettings } from "./details/helpers";
import { textSearchSettingsTitles } from "./textSearch/constants";

type SettingsTitles<T> = {
  [T2 in keyof T]: string;
};

export const campaignTypes: CampaignType[] = [
  "VPR",
  "VPC",
  "VPS",
  "VPD",
  "TEXT_SEARCH",
];

export const campaignTypeTitles: Record<CampaignType, string> = {
  VPR: "Similar Products",
  VPS: "Visual Search",
  VPC: "Cross-Selling",
  VPD: "Product Data",
  TEXT_SEARCH: "Text Search",
};

export const switchableSettingsTitles: SwitchableSettingsTitles = {
  active: "active",
  useObjectDetection: "object detection",
  ageGroupSensitive: "age groups",
  genderSensitive: "gender",
  onlyBuyable: "only buyable products",
  isDefault: "default campaign",
  categoryFilterEnabled: "category",
  ageGroupFilterEnabled: "age groups",
  genderFilterEnabled: "gender",
  filterObjectByRelevantClasses: "relevant classes filtering",
  filterObjectByCatalogSimilarity: "catalog similarity filtering",
  ocrActive: "OCR",
  barcodeReaderActive: "Barcode reader",
};

export const textCampaignInfluencersTitles: TextCampaignInfluencersTitles = {
  exactSearchActive: "Exact Match First",
  diversificationActive: "Result Diversification",
  exactSearchOnly: "Exact Search Only",
};

export const textCampaignInfluencersKeys: TextCampaignInfluencersKeys[] = [
  "exactSearchActive",
  "diversificationActive",
  "exactSearchOnly",
];

const titleTextSurrounding = ["", "Weight", ...netTextSurrounding];

const similarityNetsTitles = keysWithTitles<
  Pick<NumericSettingsTitles, SimilarityAITitles>
>(minimalTextTitleSimilarity, ...titleTextSurrounding);

const colorNetsTitles = keysWithTitles<
  Pick<NumericSettingsTitles, ColorAiTitles>
>(minimalTextTitleColor, ...titleTextSurrounding);

export const campaignPercentageSettings: (keyof CampaignBody)[] = [
  "priceWeight",
  "categorySensitivityWeight",
  "priceRelativeInflectionPoint",
  "minimumDistance",
  "maximumDistance",
  "resultDistanceThreshold",
  "objectDetectionThreshold",
  "colorFamilyThreshold",
  "colorThreshold",
];

export const numericSettingsTitles: NumericSettingsTitles = {
  priceWeight: "Price sensitivity",
  categorySensitivityWeight: "Category sensitivity",
  priceRelativeInflectionPoint: "Reasonable price difference",
  minimumDistance: "Minimum Similarity Difference",
  maximumDistance: "Maximum Similarity Difference",
  resultDistanceThreshold: "Minimum Pairwise Difference",
  objectDetectionThreshold: "Object detection acceptable confidence",
  maximumResults: "maximum results",
  ...similarityNetsTitles,
  ...colorNetsTitles,
  priceScalingFactor: "Scaling factor",
  gingerTeaMaximumObjects: "Maximum Objects",
  numberOfDisplayedDynamicFilters: "Displayed Dynamic Filters",
  exactSearchMaximumResults: "maximum exact results",
};

export const colorSettingsTitles = {
  colorFamilyThreshold: "Color Family Threshold",
  maximumColorFamilies: "Maximum Color Families",
  colorThreshold: "Color Threshold",
};

export const selectSettingsTitles: SelectSettingsTitles = {
  categorySensitivityColumn: "Category Sensitivity Type",
  categoryFilterType: "Category Filtering Type",
  preferredImage: "Preferred Image",
  objectDetector: "Object Detector",
  gingerTeaRelevantObjectClasses: "Relevant Object Classes",
  gingerTeaCategoryTree: "Category Tree",
};

export const controlledInputTitles = {
  ...numericSettingsTitles,
  ...colorSettingsTitles,
  ...textSearchSettingsTitles,
  ...selectSettingsTitles,
  ...textCampaignInfluencersTitles,
  ...switchableSettingsTitles,
};

export const primitiveSettingsTitles: SettingsTitles<{ name: string }> = {
  name: "name",
};

export const availabilitySwitchableSettings: (keyof SwitchableSettingsTitles)[] =
  ["onlyBuyable"];

export const generalInformationSwitchableSettings: (keyof SwitchableSettingsTitles)[] =
  ["active", "isDefault"];

export const objectDetectionSwitchableSettings: (keyof SwitchableSettingsTitles)[] =
  [
    "useObjectDetection",
    "filterObjectByRelevantClasses",
    "filterObjectByCatalogSimilarity",
  ];

const filtersSwitchableSettings = (
  Object.keys(switchableSettingsTitles) as (keyof SwitchableSettingsTitles)[]
).filter(
  (switchableSetting) =>
    !generalInformationSwitchableSettings.includes(switchableSetting) &&
    !objectDetectionSwitchableSettings.includes(switchableSetting) &&
    !availabilitySwitchableSettings.includes(switchableSetting),
);

export const influencersAttributesSwitchableSettings: (keyof SwitchableSettingsTitles)[] =
  filtersSwitchableSettings.filter(
    (switchableSetting) =>
      ![
        "genderFilterEnabled",
        "categoryFilterEnabled",
        "ageGroupFilterEnabled",
      ].includes(switchableSetting),
  );

export const similarityNetNumericSettings: SimilarityAITitles[] = [
  "berryPunchWeight",
  "darjeelingSecondFlushWeight",
  "volcanicWaterWeight",
];

export const colorNetNumericSettings: ColorAiTitles[] = ["kibaWeight"];

export const campaignNetsKeys = [
  ...similarityNetNumericSettings,
  ...colorNetNumericSettings,
];

export const similarityNumericSettings: (keyof NumericSettingsTitles)[] = [
  "minimumDistance",
  "maximumDistance",
  "resultDistanceThreshold",
];

export const priceImpactNumericSettings: (keyof NumericSettingsTitles)[] = [
  "priceWeight",
  "priceRelativeInflectionPoint",
  "priceScalingFactor",
];

export const categoryImpactNumericSettings: (keyof NumericSettingsTitles)[] = [
  "categorySensitivityWeight",
];

export const colorNumericSettings: (keyof typeof colorSettingsTitles)[] = [
  "maximumColorFamilies",
  "colorFamilyThreshold",
  "colorThreshold",
];

export const controlledNumberSettings = {
  type: "number",
  min: 0,
  max: 100,
};

export const percentageNumberSettings = {
  ...controlledNumberSettings,
  defaultValue: 0,
};

export const netsModalSettings = Object.fromEntries(
  campaignNetsKeys.map((net) => [net, percentageNumberSettings]),
) as Record<CampaignNetKey, typeof percentageNumberSettings>;

export const numericModalSettings = {
  maximumResults: {
    ...percentageNumberSettings,
    defaultValue: 12,
    step: 1,
  },
  ...netsModalSettings,
  categorySensitivityWeight: percentageNumberSettings,
  priceWeight: percentageNumberSettings,
  priceRelativeInflectionPoint: {
    ...percentageNumberSettings,
    defaultValue: 30,
  },
  priceScalingFactor: { defaultValue: 1.15, min: 0, max: 99999, step: 0.01 },
  minimumDistance: percentageNumberSettings,
  maximumDistance: { ...percentageNumberSettings, defaultValue: 100 },
  resultDistanceThreshold: percentageNumberSettings,
  objectDetectionThreshold: {
    ...percentageNumberSettings,
    defaultValue: 50,
  },
  gingerTeaMaximumObjects: { defaultValue: 4 },
  numberOfDisplayedDynamicFilters: { min: 0 },
  exactSearchMaximumResults: { min: 0 },
};

export const colorModalSettings = {
  colorFamilyThreshold: percentageNumberSettings,
  maximumColorFamilies: {
    type: "number",
    min: 1,
    max: 255,
  },
  colorThreshold: percentageNumberSettings,
};

export const selectorModalsTitles: SettingsTitles<Selector> = {
  type: "campaign type",
  categoryFilterType: "category filtering type",
};

const categorySensitivityColumns: CategorySensitivityColumns[] = [
  "product_type",
  "google_product_category",
];

export const categorySensitivityTypeSettings = valuesToSettings(
  categorySensitivityColumns,
);

const categoryFilterType: CategoryFilterType[] = [
  "product_type_category",
  "google_product_category",
];
export const categoryFilterTypeSettings = valuesToSettings(categoryFilterType);

const campaignPreferredImageEnum: CampaignPreferredImageEnum[] = [
  "first",
  "best_match",
];

export const preferredImageSettings = valuesToSettings(
  campaignPreferredImageEnum,
);
